import * as React from 'react';
import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import TableContainer from '@mui/material/TableContainer';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import FileSaver from 'file-saver';
import { graphql } from '../../../__generated__';
import TableHeader from './TableHeader';
import TransactionRow from './TransactionRow';
import { pollServer } from '../../../graphql/utils';
import { refreshRequestsVar } from '../common';
import { addDays } from 'date-fns';
import { getDownloadUrl } from '../../../data/firebase';
import { getStartOfDay, getEndOfDay } from '../../../util/dates';

export const allTransactionsQuery = graphql(/* GraphQL */ `
  query GetAllTransactions(
    $searchText: String
    $pageSize: Int
    $startPage: Int
    $sortBy: String
    $sortOrder: String
  ) {
    transactions(
      searchText: $searchText
      pageSize: $pageSize
      startPage: $startPage
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      ...Transaction_RowFragment
    }
  }
`);

export const exportTransactionsQuery = graphql(/* GraphQL */ `
  query ExportTransactions($from: String!, $to: String!) {
    exportTransactions(from: $from, to: $to)
  }
`);

const MIN_ROWS_PER_PAGE = 5;

const TransactionTable = () => {
  const [text] = React.useState<string>('');
  const [pageSize, setPageSize] = React.useState(50);
  const [startDate, setStartDate] = React.useState(
    addDays(getStartOfDay(new Date()), -30)
  );
  const [endDate, setEndDate] = React.useState(getEndOfDay(new Date()));
  const [sortProps, setSortProps] = React.useState<{
    by: string;
    order: 'asc' | 'desc';
  }>({ by: 'date', order: 'asc' });
  const [pageError, setPageError] = React.useState<string>('');
  const [startPage, setStartPage] = React.useState(1);
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
    startPolling,
    stopPolling,
  } = useQuery(allTransactionsQuery, {
    variables: {
      searchText: text,
      pageSize,
      startPage,
      sortBy: sortProps.by,
      sortOrder: sortProps.order,
    },
  });
  const [exportTransactions, { loading: downloading, error: downloadError }] =
    useLazyQuery(exportTransactionsQuery, {
      variables: {
        from: `${startDate.getTime()}`,
        to: `${endDate.getTime()}`,
      },
    });

  React.useEffect(() => {
    refetch({
      searchText: text,
      pageSize,
      startPage: 0,
      sortBy: sortProps.by,
      sortOrder: sortProps.order,
    });
  }, [text, pageSize, refetch, sortProps]);

  const handleRequestSort = React.useMemo(
    () => (property: string) => {
      const { by, order } = sortProps;
      const isCurrentlyAsc = by === property && order === 'asc';
      setSortProps({
        by: property,
        order: isCurrentlyAsc ? 'desc' : 'asc',
      });
    },
    [sortProps]
  );

  const shouldRefresh = useReactiveVar(refreshRequestsVar);
  React.useEffect(() => {
    if (shouldRefresh) {
      refreshRequestsVar(false);
      pollServer(startPolling, stopPolling);
    }
  }, [shouldRefresh, startPolling, stopPolling]);

  const handleChangePage = (_event: any, newPage: number) => {
    setStartPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: { target: { value: string } }) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  const downloadCsv = async () => {
    const res = await exportTransactions();
    if (res.data?.exportTransactions && res.data.exportTransactions !== null) {
      try {
        const url = await getDownloadUrl(res.data.exportTransactions);
        FileSaver.saveAs(url, res.data.exportTransactions);
      } catch (err) {
        setPageError(JSON.stringify(err));
      }
    }
  };

  const rowDisplayFunc = ({
    from,
    to,
    count,
  }: {
    from: number;
    to: number;
    count: number;
  }) =>
    from > to
      ? 'no more'
      : `${from}-${to === -1 ? count : to} of ${
          count !== -1 ? count : `${to} or more`
        }`;

  const transactions = data?.transactions || [];
  const maxDate = new Date();

  const loading = queryLoading || downloading;
  const error = queryError?.message || downloadError?.message || pageError;
  return (
    <Box sx={{ padding: 2 }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container justifyContent="flex-end" spacing={2}>
          <Grid xs={4} md={2}>
            <DatePicker
              label="From"
              maxDate={maxDate}
              defaultValue={startDate}
              slotProps={{ textField: { size: 'small' } }}
              sx={{ mr: 1 }}
              onChange={(v) => {
                if (v !== null) setStartDate(getStartOfDay(v));
              }}
            />
          </Grid>
          <Grid xs={4} md={2}>
            <DatePicker
              label="To"
              maxDate={maxDate}
              defaultValue={endDate}
              slotProps={{ textField: { size: 'small' } }}
              onChange={(v) => {
                if (v !== null) setEndDate(getEndOfDay(v));
              }}
            />
          </Grid>
          <Grid xs={4} md={2}>
            <Button variant="contained" onClick={downloadCsv}>
              Download
            </Button>
          </Grid>
        </Grid>
      </LocalizationProvider>
      <Paper sx={{ mt: 3, mb: 3 }}>
        {loading && <LinearProgress />}
        {error && (
          <Alert severity="error" onClose={() => refetch()}>
            {error}
          </Alert>
        )}
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            size="small"
            aria-label="transactions"
            aria-labelledby="tableTitle"
          >
            <TableHeader
              orderBy={sortProps.order}
              sortOrder={sortProps.order}
              onRequestSort={handleRequestSort}
              key="table-header"
            />
            <TableBody>
              {transactions.map((trx, index) => {
                if (trx === null) return null;
                return <TransactionRow transaction={trx} key={index} />;
              })}
              {transactions.length < MIN_ROWS_PER_PAGE && (
                <TableRow
                  style={{
                    height: 53 * (MIN_ROWS_PER_PAGE - transactions.length),
                  }}
                >
                  <TableCell colSpan={11} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[50, 100, 200]}
          component="div"
          count={transactions.length}
          rowsPerPage={pageSize}
          page={startPage - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={rowDisplayFunc}
        />
      </Paper>
    </Box>
  );
};

export default TransactionTable;
